export interface AppError {
  code: string;
  message: string;
  messageKey?: string;
  messageParams?: Record<string, string | number | boolean>;
  reason?: string;
}

export type ErrorMessageTranslator = (key: string, options?: Record<string, unknown>) => string;

interface AppErrorMessage {
  message: string;
  messageKey?: string;
  messageParams?: Record<string, string | number | boolean>;
}

const ERROR_MESSAGES: Record<string, string> = {
  'permission-denied': 'Non hai i permessi per questa azione',
  'not-found': 'Risorsa non trovata',
  unavailable: 'Servizio temporaneamente non disponibile',
  unauthenticated: 'Devi effettuare il login per continuare',
  'invalid-argument': 'Dati non validi',
  'already-exists': 'La risorsa esiste già',
  'resource-exhausted': 'Limite di richieste raggiunto, riprova più tardi',
  cancelled: 'Operazione annullata',
  'data-loss': 'Errore nella gestione dei dati',
  unknown: 'Errore imprevisto',
  internal: 'Errore interno del server',
  'invalid-credential': 'Credenziali non valide',
  'wrong-password': 'Password non corretta',
  'user-not-found': 'Utente non trovato',
  'user-disabled': 'Account disabilitato',
  'email-already-in-use': 'Email già in uso',
  'weak-password': 'Password troppo debole',
  'too-many-requests': 'Troppi tentativi, riprova più tardi',
  'network-request-failed': 'Errore di connessione',
  'reports/fetch-failed': 'Impossibile caricare le segnalazioni',
  'report/fetch-failed': 'Impossibile caricare la segnalazione',
  'report/response-validation': 'Risposta non valida dal backend delle segnalazioni',
  'report/photo-access-failed': 'Impossibile caricare le foto della segnalazione',
  'report/update-failed': 'Impossibile aggiornare la segnalazione',
  'reports/invalid-query': 'Filtri segnalazioni non validi',
  'reports/response-validation': 'Risposta non valida dal backend delle segnalazioni',
  'auth/reset-password-failed': "Impossibile inviare l'email di reset",
  'export/failed': 'Impossibile esportare le segnalazioni',
  'user-account/list-failed': 'Impossibile caricare gli account utente',
  'user-account/create-failed': "Impossibile creare l'account utente",
  'user-account/update-failed': "Impossibile aggiornare l'account utente",
  'user-account/delete-failed': "Impossibile eliminare l'account utente",
  'user-account/invalid-query': 'Filtri account utente non validi',
  'user-account/export-failed': 'Impossibile esportare gli account utente',
  'user-account/export-result-too-large':
    "Troppi account utente per l'esportazione corrente, restringi i filtri",
  'user-account/export-planner-unavailable':
    "L'esportazione degli account utente non è disponibile finché manca l'indice richiesto",
  'user-account/response-validation': 'Risposta non valida dal backend degli account utente',
};

const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  password: 'Password',
  displayName: 'Nome visualizzato',
  uid: 'UID account utente',
};

const BACKEND_MESSAGE_TRANSLATIONS: Record<string, AppErrorMessage> = {
  'Email already in use': {
    message: 'Email già in uso',
    messageKey: 'users:userManagement.backendErrors.emailAlreadyInUse',
  },
  'User not found': {
    message: 'Account utente non trovato',
    messageKey: 'users:userManagement.backendErrors.userNotFound',
  },
  'Authentication required': {
    message: 'Devi effettuare il login per continuare',
    messageKey: 'users:userManagement.backendErrors.authenticationRequired',
  },
};

function isErrorCode(error: unknown): error is { code: string } {
  return typeof error === 'object' && error !== null && 'code' in error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFirebaseError(error: unknown): error is { code: string } {
  if (!isRecord(error) || typeof error.code !== 'string') {
    return false;
  }

  return error.name === 'FirebaseError' || error.constructor?.name === 'FirebaseError';
}

function normalizeErrorCode(code: string): string {
  return code.startsWith('functions/') ? code.slice('functions/'.length) : code;
}

function getMessageForCode(code: string, fallbackCode?: string): string | undefined {
  const unknownCode = fallbackCode || 'unknown';
  return (
    ERROR_MESSAGES[code] || ERROR_MESSAGES[normalizeErrorCode(code)] || ERROR_MESSAGES[unknownCode]
  );
}

function getValidationDetails(error: unknown): unknown[] | undefined {
  if (!isRecord(error)) {
    return undefined;
  }

  if (Array.isArray(error.details)) {
    return error.details;
  }

  if (isRecord(error.customData) && Array.isArray(error.customData.details)) {
    return error.customData.details;
  }

  return undefined;
}

function getFirstPathSegment(detail: Record<string, unknown>): string | undefined {
  const { path } = detail;
  if (!Array.isArray(path) || path.length === 0) {
    return undefined;
  }

  const [firstPathSegment] = path;
  return typeof firstPathSegment === 'string' ? firstPathSegment : undefined;
}

function getTranslatedValidationDetailMessage(
  detail: Record<string, unknown>
): AppErrorMessage | undefined {
  const field = getFirstPathSegment(detail);
  const code = typeof detail.code === 'string' ? detail.code : undefined;
  const minimum = typeof detail.minimum === 'number' ? detail.minimum : undefined;
  const maximum = typeof detail.maximum === 'number' ? detail.maximum : undefined;
  const message = typeof detail.message === 'string' ? detail.message : undefined;

  if (field === 'password') {
    if (code === 'too_small' && minimum !== undefined) {
      return {
        message: `La password deve contenere almeno ${minimum} caratteri`,
        messageKey: 'users:userManagement.backendValidation.passwordMinLength',
        messageParams: { minimum },
      };
    }
    if (code === 'invalid_type') {
      return {
        message: 'La password è obbligatoria',
        messageKey: 'users:userManagement.validation.passwordRequired',
      };
    }
  }

  if (field === 'email') {
    if (code === 'invalid_format' || message === 'Invalid email format') {
      return {
        message: 'Indirizzo email non valido',
        messageKey: 'users:userManagement.validation.invalidEmail',
      };
    }
    if (code === 'invalid_type') {
      return {
        message: "L'email è obbligatoria",
        messageKey: 'users:userManagement.validation.emailRequired',
      };
    }
  }

  if (field === 'displayName') {
    if (code === 'too_small') {
      return {
        message: 'Il nome visualizzato è obbligatorio',
        messageKey: 'users:userManagement.validation.displayNameRequired',
      };
    }
    if (code === 'too_big' && maximum !== undefined) {
      return {
        message: `Il nome visualizzato deve contenere al massimo ${maximum} caratteri`,
        messageKey: 'users:userManagement.backendValidation.displayNameMaxLength',
        messageParams: { maximum },
      };
    }
  }

  if (field === 'uid' && code === 'too_small') {
    return {
      message: "L'UID account utente è obbligatorio",
      messageKey: 'users:userManagement.backendValidation.uidRequired',
    };
  }

  if (message === 'At least one field besides uid must be provided') {
    return {
      message: "Indica almeno un campo da aggiornare oltre all'UID",
      messageKey: 'users:userManagement.backendValidation.updateFieldRequired',
    };
  }

  return undefined;
}

function getValidationDetailMessage(detail: unknown): AppErrorMessage | undefined {
  if (!isRecord(detail)) {
    return undefined;
  }

  const translatedMessage = getTranslatedValidationDetailMessage(detail);
  if (translatedMessage) {
    return translatedMessage;
  }

  const message = typeof detail.message === 'string' ? detail.message : undefined;
  if (!message) {
    return undefined;
  }

  const field = getFirstPathSegment(detail);
  const fieldLabel = field ? FIELD_LABELS[field] : undefined;

  return { message: fieldLabel ? `${fieldLabel}: ${message}` : message };
}

function getValidationErrorMessage(error: unknown): AppErrorMessage | undefined {
  const messages = getValidationDetails(error)
    ?.map(getValidationDetailMessage)
    .filter((message): message is AppErrorMessage => Boolean(message));

  if (!messages || messages.length === 0) {
    return undefined;
  }

  if (messages.length === 1) {
    return messages[0];
  }

  return {
    message: messages.map((message) => message.message).join('; '),
  };
}

function getTranslatedBackendMessage(error: unknown): AppErrorMessage | undefined {
  if (!isRecord(error) || typeof error.message !== 'string') {
    return undefined;
  }

  if (typeof error.code === 'string' && !error.code.startsWith('functions/')) {
    return undefined;
  }

  return BACKEND_MESSAGE_TRANSLATIONS[error.message];
}

function toAppErrorMessage(message: string): AppErrorMessage {
  return { message };
}

function createAppError(code: string, message: AppErrorMessage): AppError {
  return {
    code,
    message: message.message,
    messageKey: message.messageKey,
    messageParams: message.messageParams,
  };
}

export function mapFirebaseError(error: unknown, fallbackCode?: string): AppError {
  const unknownCode = fallbackCode || 'unknown';

  if (isErrorCode(error)) {
    const validationMessage = getValidationErrorMessage(error);
    const backendMessage = getTranslatedBackendMessage(error);
    const mappedMessage = getMessageForCode(error.code) || 'Errore imprevisto';

    return createAppError(
      error.code,
      validationMessage || backendMessage || toAppErrorMessage(mappedMessage)
    );
  }

  if (error instanceof Error) {
    if (fallbackCode) {
      return {
        code: fallbackCode,
        message: ERROR_MESSAGES[fallbackCode] || ERROR_MESSAGES[unknownCode] || 'Errore imprevisto',
      };
    }
    return {
      code: unknownCode,
      message: ERROR_MESSAGES[unknownCode] || 'Errore imprevisto',
    };
  }

  return {
    code: unknownCode,
    message: ERROR_MESSAGES[unknownCode] || 'Errore imprevisto',
  };
}

export function getErrorMessage(error: unknown): string {
  return mapFirebaseError(error).message;
}

export function getAppErrorMessage(error: unknown, t: unknown): string {
  if (isAppError(error)) {
    if (error.messageKey) {
      const translate = t as ErrorMessageTranslator;
      return translate(error.messageKey, error.messageParams);
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }

  return mapFirebaseError(error).message;
}

export function isPermissionDenied(error: unknown): boolean {
  return isFirebaseError(error) && error.code === 'permission-denied';
}

export function isNotFoundError(error: unknown): boolean {
  return isFirebaseError(error) && error.code === 'not-found';
}

export function isUnauthenticated(error: unknown): boolean {
  return isFirebaseError(error) && error.code === 'unauthenticated';
}

export function isNetworkError(error: unknown): boolean {
  if (isFirebaseError(error)) {
    return error.code === 'network-request-failed' || error.code === 'unavailable';
  }
  if (isAppError(error)) {
    return error.code === 'network-request-failed' || error.code === 'unavailable';
  }
  return false;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}
